/**
 * Change pill (week-over-week by default). Color convention (inherited
 * from the rest of the codebase): price dropping = emerald
 * (buyer-positive), price rising = rose, below the noise threshold or a
 * rounded zero = gray.
 *
 * The value is rounded to one decimal before anything is decided, so a
 * rounded zero reads "0%" in gray with no arrow and no sign (never
 * "+0.0%" / "-0.0%"). A gray non-zero value keeps its sign in the text
 * since there is no arrow to carry it.
 */
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { changeSign, roundChange, signedPct } from "@/lib/format/change";

interface Props {
  wow: number | null | undefined;
  /** threshold below which the change is treated as noise (default 0.2) */
  threshold?: number;
  /** "sm" renders the smaller variant used in grid cards */
  size?: "sm" | "md";
}

export default function WoWBadge({ wow, threshold = 0.2, size = "md" }: Props) {
  const t = useT();
  const sign = changeSign(wow, 1);
  const rounded = wow == null ? null : roundChange(wow, 1);
  const isNeutral = rounded == null || sign === 0 || Math.abs(rounded) < threshold;
  const isUp = !isNeutral && sign > 0;

  const Icon = isNeutral ? null : isUp ? ArrowUpRight : ArrowDownRight;

  const colorClass = isNeutral
    ? "bg-gray-100 text-gray-500"
    : isUp
      ? "bg-rose-50 text-rose-600 dark:text-rose-400"
      : "bg-emerald-50 text-emerald-700 dark:text-emerald-400";

  const textSize = size === "sm" ? "text-[11px]" : "text-xs sm:text-sm";
  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  const padding = size === "sm" ? "px-1.5 py-0.5" : "px-2 py-1";

  const text = rounded == null
    ? t("common.na")
    : Icon
      ? `${Math.abs(rounded).toFixed(1)}%`
      : signedPct(rounded, 1);

  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full font-semibold tabular-nums ${colorClass} ${textSize} ${padding}`}
    >
      {Icon && <Icon className={iconSize} />}
      {text}
    </span>
  );
}
